import React from 'react';
import { infraIdRegion, Node } from '../app/components/new-canvas/InfraStructure';
import { RepoUpdateStatus, DiffSummary, GraphChanges, CodeRescanMode, RepoScanCommitInfo } from '../common/api';
import { CodeViewEdge, CodeViewNode } from '../app/components/new-canvas/canvas.component';
import { Input } from '../app/components/new-view-edit-architecture/components/AddMultipleGitRepoModal/AddMultipleGitRepoModal';
import { ChatMessage } from '../types/chat';
import type { V2CanvasViewMode } from '../app/components/canvas-v2/v2SidebarData';

export interface ContextType {
    theme: string;
    toggleTheme: () => void;
    showSpinner: boolean;
    openSpinnerHandler: () => void;
    closeSpinnerHandler: () => void;
    activeSection: string;
    setActiveSection: (section: string) => void;
    highlightEdgesContext: { key: any; nodes: any[] };
    setHighlightEdgesContext: (value: { key: any; nodes: any[] }) => void;
    highlightNodesContext: any[];
    setHighlightNodesContext: (nodes: any[]) => void;
    searchNode: any;
    setSearchNode: (node: any) => void;
    showBars: { showNavbar: boolean; showLeftSidebar: boolean; showFooter: boolean };
    setShowBars: React.Dispatch<React.SetStateAction<{ showNavbar: boolean; showLeftSidebar: boolean; showFooter: boolean }>>;
    footerSearchText: string;
    setFooterSearchText: (text: string) => void;
    useCache: boolean;
    setUseCache: (use: boolean) => void;
    projects: any[];
    setProjects: (projects: any[]) => void;
    footerBtn: any;
    setFooterBtn: (btn: any) => void;
    showDocSidebarCanvas: boolean;
    setDocSidebarCanvas: (show: boolean) => void;
    isTourOpen: boolean;
    setIsTourOpen: (open: boolean) => void;
    edgeColor: any;
    setEdgeColor: (color: any) => void;
    zoomVal: any;
    setZoomVal: (val: any) => void;
    userId: any;
    setUserId: (id: any) => void;
    showToastr: { show: boolean; type: "error" | "success" | "info" | "progress" | "warning"; text: React.ReactNode };
    setShowToastr: React.Dispatch<React.SetStateAction<{ show: boolean; type: "error" | "success" | "info" | "progress" | "warning"; text: React.ReactNode }>>;
    isAnyApplicationSelected: boolean;
    setIsAnyApplicationSelected: (selected: boolean) => void;
    queryStr: string;
    setQueryStr: (str: string) => void;
    selectedInfraviewDetail: any[];
    setSelectedInfraviewDetail: React.Dispatch<React.SetStateAction<any[]>>;
    matchedServices: string;
    setMatchedServices: (services: string) => void;
    matchedNodeDetail: any[];
    setMatchedDetails: (details: any[]) => void;
    searchResults: Node[];
    setSearchbarResults: (results: Node[]) => void;
    showReviewSidebar: boolean;
    setShowReviewSidebar: (show: boolean) => void;
    reviewSidebarNodeId: string | null;
    setReviewSidebarNodeId: (id: string | null) => void;
    nodeLabel: any[];
    setNodeLabel: (labels: any[]) => void;
    showAddNewNodeModal: any;
    setShowAddNewNodeModal: (modal: any) => void;
    linkedNodeDetail: CodeViewNode[];
    setLinkedNodeDetail: React.Dispatch<React.SetStateAction<CodeViewNode[]>>;
    nodeJmId: CodeViewNode[];
    setNodeJmlId: React.Dispatch<React.SetStateAction<CodeViewNode[]>>;
    linkedNode: CodeViewNode[];
    setLinkedNode: React.Dispatch<React.SetStateAction<CodeViewNode[]>>;
    cloudConnection: boolean;
    setCloudConnection: (connected: boolean) => void;
    buttonClicked: boolean;
    setButtonClicked: (clicked: boolean) => void;
    connectedAccount: any[];
    setConnectedAccount: React.Dispatch<React.SetStateAction<any[]>>;
    disableAskAi: boolean;
    setDisableAskAi: (disable: boolean) => void;
    /** V2/V3 scan: architecture graph is ready but background post-processing is still running. */
    v2PostProcessingPending: boolean;
    setV2PostProcessingPending: React.Dispatch<React.SetStateAction<boolean>>;
    reactFlowInstance: any;
    setReactFlowInstance: (instance: any) => void;
    isSimpleElk: boolean;
    setIsSimpleElk: (simple: boolean) => void;
    isRenaming: boolean;
    setIsRenaming: (renaming: boolean) => void;
    edgeForRename: any;
    setEdgeForRename: (edge: any) => void;
    selectNodeForLinking: CodeViewNode | null;
    setSelectNodeForLinking: React.Dispatch<React.SetStateAction<CodeViewNode | null>>;
    isMatchRef: React.MutableRefObject<boolean>;
    openZoomModal: boolean;
    setOpenZoomModal: (open: boolean) => void;
    openInfraAskAi: boolean;
    setOpenInfraAskAi: (open: boolean) => void;
    graphNodes: CodeViewNode[];
    setGraphNodes: (nodes: CodeViewNode[]) => void;
    graphEdges: CodeViewEdge[];
    setGraphEdges: (edges: CodeViewEdge[]) => void;
    /** V2 canvas tab: code (technical) or business (doc) view — used by Ask AI. */
    v2CanvasView: V2CanvasViewMode;
    setV2CanvasView: (view: V2CanvasViewMode) => void;
    /** Which AI View flow (index into architecture.ai_view.flows) is currently displayed —
     *  shared between CanvasV2Tabs' canvas selection and the left sidebar's summary/dataflow. */
    v2AiFlowIndex: number;
    setV2AiFlowIndex: (index: number) => void;
    graphView: boolean;
    setGraphView: (view: boolean) => void;
    linkedjmlId: any;
    setLinkedjmlId: (id: any) => void;
    editMode: boolean;
    setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
    isDataUpdated: boolean;
    setIsDataUpdated: React.Dispatch<React.SetStateAction<boolean>>;
    codeConnectRequestIdRef: React.MutableRefObject<string | null>;
    generateCodeRequestIdRef: React.MutableRefObject<string | null>;
    retrieveRequestIdRef: React.MutableRefObject<string | null>;
    inferRepoRequestIdRef: React.MutableRefObject<string | null>;
    infraIdRegion: infraIdRegion;
    setInfraIdRegion: (infraIdRegion: infraIdRegion) => void;
    showSuccessToastRef: React.MutableRefObject<boolean>;
    autoConnectPendingRef: React.MutableRefObject<{ requestId: string; input: Input } | null>;
    groupViewAvailable: boolean;
    setGroupViewAvailable: (available: boolean) => void;
    expandedCodeNodeIds: string[];
    setExpandedCodeNodeIds: (ids: string[]) => void;
    showCostOnNodes: boolean;
    setShowCostOnNodes: (show: boolean) => void;
    connectedUrls: Record<string, string[]>;
    setConnectedUrls: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
    // Repository update tracking
    repoUpdates: RepoUpdateStatus[];
    setRepoUpdates: React.Dispatch<React.SetStateAction<RepoUpdateStatus[]>>;
    diffSummary: DiffSummary | null;
    setDiffSummary: React.Dispatch<React.SetStateAction<DiffSummary | null>>;
    activeDiffGraphChanges: GraphChanges | null;
    setActiveDiffGraphChanges: React.Dispatch<React.SetStateAction<GraphChanges | null>>;
    // Progressive pipeline phases
    phase1Complete: boolean;
    setPhase1Complete: React.Dispatch<React.SetStateAction<boolean>>;
    phase2Complete: boolean;
    setPhase2Complete: React.Dispatch<React.SetStateAction<boolean>>;
    isLoadingPhase2: boolean;
    setIsLoadingPhase2: React.Dispatch<React.SetStateAction<boolean>>;
    hasPostScanCompleted: boolean;
    setHasPostScanCompleted: React.Dispatch<React.SetStateAction<boolean>>;
    showAddMultipleGitRepoModal: boolean;
    setShowAddMultipleGitRepoModal: React.Dispatch<React.SetStateAction<boolean>>;
    /** Repo URL to prefill in AddMultipleGitRepoModal when opened for reconnecting (stale/expired token). */
    reconnectRepoUrl: string | null;
    setReconnectRepoUrl: React.Dispatch<React.SetStateAction<string | null>>;
    showAddConnectCloudModal: boolean;
    setShowAddConnectCloudModal: React.Dispatch<React.SetStateAction<boolean>>;
    // Chat state sharing between footer and full chat
    chatMessages: ChatMessage[];
    setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
    currentThreadId: string | null;
    setCurrentThreadId: (threadId: string | null) => void;
    pendingQuestion: string | null;
    setPendingQuestion: (question: string | null) => void;
    isWaitingForResponse: boolean;
    setIsWaitingForResponse: React.Dispatch<React.SetStateAction<boolean>>;
    currentRequestId: string | null;
    setCurrentRequestId: (requestId: string | null) => void;
    repoScanCommits: RepoScanCommitInfo[];
    setRepoScanCommits: React.Dispatch<React.SetStateAction<RepoScanCommitInfo[]>>;
    takeSnapShotRef: React.MutableRefObject<((instance?: any) => void) | null>;
    screenshotSuccessfulRef: React.MutableRefObject<boolean>;
    activityId: string | null;
    setActivityId: React.Dispatch<React.SetStateAction<string | null>>;
    isLoadingActivityId: boolean;
    setIsLoadingActivityId: React.Dispatch<React.SetStateAction<boolean>>;
    hasPendingActions: boolean;
    setHasPendingActions: React.Dispatch<React.SetStateAction<boolean>>;
    selectedRepo: string | null;
    setSelectedRepo: React.Dispatch<React.SetStateAction<string | null>>;
    isAskAiTimeout: boolean;
    setIsAskAiTimeout: React.Dispatch<React.SetStateAction<boolean>>;
    askAiTimerRef: React.MutableRefObject<NodeJS.Timeout | null>;
    notifyOnCompletion: boolean;
    setNotifyOnCompletion: React.Dispatch<React.SetStateAction<boolean>>;
    // Project-level code rescan mode preference
    codeRescanMode: CodeRescanMode | null;
    setCodeRescanMode: React.Dispatch<React.SetStateAction<CodeRescanMode | null>>;
    rescanModeSupported: boolean;
    setRescanModeSupported: React.Dispatch<React.SetStateAction<boolean>>;
    isCompactViewDisabled: boolean;
    setIsCompactViewDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    logoutHandler: (() => void) | null;
    setLogoutHandler: React.Dispatch<React.SetStateAction<(() => void) | null>>;
}

const CreateContext = React.createContext<ContextType | undefined>(undefined);

export default CreateContext;